import React, { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { HardHat, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const LoginPage = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    
    const { login, isAuthenticated } = useAuth();
    const navigate = useNavigate();

    if (isAuthenticated) {
        return <Navigate to="/" replace />;
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            await login(email, password);
            toast.success('Bienvenido al sistema');
            navigate('/');
        } catch (err) {
            setError(err.response?.data?.detail || 'Credenciales inválidas');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex" data-testid="login-page">
            {/* Left Side - Branding */}
            <div className="hidden lg:flex lg:w-1/2 bg-[#0B3D91] relative overflow-hidden">
                {/* Background Image with Overlay */}
                <div 
                    className="absolute inset-0 bg-cover bg-center"
                    style={{
                        backgroundImage: "url('https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=1200')",
                        opacity: 0.15
                    }}
                />
                
                {/* Content */}
                <div className="relative z-10 flex flex-col items-center justify-center w-full p-12">
                    {/* Logo */}
                    <div className="w-28 h-28 bg-[#FF6B00] rounded flex items-center justify-center mb-8 shadow-2xl">
                        <HardHat className="w-16 h-16 text-white" />
                    </div>
                    
                    {/* Title */}
                    <h1 className="font-['Barlow_Condensed'] text-6xl font-bold text-white tracking-tight uppercase text-center">
                        Segumax
                    </h1>
                    <p className="font-['Barlow_Condensed'] text-2xl text-white/70 uppercase tracking-[0.3em] mt-2">
                        Planner
                    </p>
                    
                    {/* Description */}
                    <div className="mt-12 max-w-md text-center">
                        <p className="text-white/60 text-lg leading-relaxed">
                            Sistema de planificación de contenido para redes sociales
                        </p>
                    </div>

                    {/* Decorative Line */}
                    <div className="w-24 h-1 bg-[#FF6B00] mt-12"></div>
                    
                    {/* Footer */}
                    <div className="absolute bottom-8 text-white/30 text-xs uppercase tracking-[0.2em]">
                        Indumentaria de Trabajo EPP
                    </div>
                </div>
            </div>

            {/* Right Side - Login Form */}
            <div className="flex-1 flex items-center justify-center p-8 bg-slate-50">
                <div className="w-full max-w-md">
                    {/* Mobile Logo */}
                    <div className="lg:hidden flex items-center justify-center gap-3 mb-10">
                        <div className="w-14 h-14 bg-[#0B3D91] rounded flex items-center justify-center">
                            <HardHat className="w-8 h-8 text-white" />
                        </div>
                        <div>
                            <h1 className="font-['Barlow_Condensed'] text-3xl font-bold text-slate-800 uppercase tracking-tight">
                                Segumax
                            </h1>
                            <p className="text-xs text-slate-500 uppercase tracking-[0.2em]">Planner</p>
                        </div>
                    </div>

                    {/* Form Card */}
                    <div className="bg-white rounded shadow-xl border border-slate-200 p-8">
                        <div className="mb-8">
                            <h2 className="font-['Barlow_Condensed'] text-2xl font-bold text-slate-800 uppercase tracking-tight">
                                Acceder al Sistema
                            </h2>
                            <p className="text-sm text-slate-500 mt-1">
                                Ingresá tus credenciales para continuar
                            </p>
                        </div>

                        {error && (
                            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded flex items-center gap-3 text-red-700 text-sm">
                                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                                {error}
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="space-y-5">
                            <div>
                                <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                                    Email
                                </Label>
                                <Input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="tu@email.com"
                                    required
                                    className="mt-2 h-12 bg-slate-50 border-slate-200 focus:bg-white transition-colors"
                                    data-testid="input-email"
                                />
                            </div>

                            <div>
                                <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                                    Contraseña
                                </Label>
                                <div className="relative mt-2">
                                    <Input
                                        type={showPassword ? 'text' : 'password'}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        placeholder="••••••••"
                                        required
                                        className="h-12 bg-slate-50 border-slate-200 focus:bg-white transition-colors pr-12"
                                        data-testid="input-password"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                                    >
                                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                    </button>
                                </div>
                            </div>

                            <Button
                                type="submit"
                                disabled={loading}
                                className="w-full h-12 bg-[#FF6B00] hover:bg-[#e65000] text-white font-semibold uppercase tracking-wider mt-8 transition-all duration-200"
                                data-testid="btn-login"
                            >
                                {loading ? 'Verificando...' : 'Acceder al Sistema'}
                            </Button>
                        </form>

                        <div className="mt-8 pt-6 border-t border-slate-100">
                            <p className="text-xs text-center text-slate-400 uppercase tracking-wider">
                                Solo personal autorizado
                            </p>
                        </div>
                    </div>

                    {/* Demo credentials */}
                    <div className="mt-6 p-5 bg-white border border-slate-200 rounded shadow-sm">
                        <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">
                            Usuarios de prueba
                        </p>
                        <div className="space-y-2 text-sm text-slate-600">
                            <div className="flex items-center gap-2">
                                <span className="w-16 text-xs font-medium text-[#0B3D91] uppercase">Admin:</span>
                                <span>admin@segumax.com / admin123</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="w-16 text-xs font-medium text-slate-400 uppercase">Cliente:</span>
                                <span>cliente@segumax.com / cliente123</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LoginPage;
